"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.formatCurrency = exports.handleNegativeNumber = exports.addCurrencySymbol = exports.removeZerosIfWholeNumber = exports.addThousandsSeparator = exports.formatNumberWithTwoDecimalPlaces = void 0;

/**
 * formatNumberWithTwoDecimalPlaces - Helper function that takes an integer and returns a formatted string with two decimal places.
 *
 * @param dollarAmount - integer
 * @param decimalSeparator - string ( '.' | ',')
 *
 * @returns string
 */
var formatNumberWithTwoDecimalPlaces = function formatNumberWithTwoDecimalPlaces() {
  var dollarAmount = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  var decimalSeparator = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '.';
  var correctedDollarAmount = typeof dollarAmount === 'string' ? dollarAmount.replace(/,/g, '') : dollarAmount;
  return (parseFloat(correctedDollarAmount || 0) / 100).toFixed(2).replace('.', decimalSeparator);
};
/**
 * addThousandsSeparator - Helper function that takes a dollar amount string and adds a separator for the thousand groupings.
 *
 * @param dollarAmount - string (formatted dollar amount)
 * @param decimalSeparator - string ( '.' | ',')
 * @param thousandsSeparator - string ( '.' | ',' | '&nbsp;')
 *
 * @returns string
 */


exports.formatNumberWithTwoDecimalPlaces = formatNumberWithTwoDecimalPlaces;

var addThousandsSeparator = function addThousandsSeparator() {
  var dollarAmount = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '0';
  var decimalSeparator = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '.';
  var thousandsSeparator = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : ',';
  var numberParts = dollarAmount.toString().split(decimalSeparator);
  var integerPart = numberParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
  var decimalPart = numberParts[1];
  return decimalPart ? "".concat(integerPart).concat(decimalSeparator).concat(decimalPart) : integerPart;
};
/**
 * removeZerosIfWholeNumber - Helper function that removes trailing zeros from a whole number. For example, '3.00' becomes '3'.
 *
 * @param dollarAmount - string (formatted dollar amount)
 * @param decimalSeparator - string ( '.' | ',')
 *
 * @returns string
 */


exports.addThousandsSeparator = addThousandsSeparator;

var removeZerosIfWholeNumber = function removeZerosIfWholeNumber() {
  var dollarAmount = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '0.00';
  var decimalSeparator = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '.';
  return dollarAmount.toString().replace("".concat(decimalSeparator, "00"), '');
};
/**
 * addCurrencySymbol - Helper function that adds the currency symbol or currency code before or after the formatted dollar amount
 *
 * @param dollarAmount - string (formatted dollar amount)
 * @param symbol - string ( '$' | '£' | 'HK$' | '€' | 'MXN' ) etc.
 * @param symbolPlacement - string ( 'before' | 'after' | 'HK$' | '€' | 'MXN' ) etc.
 * @param needsExtraSpaceAdded - Boolean (places a space between the dollar amount and the currency symbol)

 * @returns string
 */


exports.removeZerosIfWholeNumber = removeZerosIfWholeNumber;

var addCurrencySymbol = function addCurrencySymbol() {
  var dollarAmount = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '0.00';
  var symbol = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '$';
  var symbolPlacement = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'before';
  var needsExtraSpaceAdded = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  // Need a non-breaking space for things like the $ symbol or € symbol after an amount
  var extraSpace = needsExtraSpaceAdded ? '&nbsp;' : '';

  if (needsExtraSpaceAdded && symbol !== '$' && symbol !== '£' && symbol !== 'HK$' && symbol !== '€') {
    extraSpace = ' ';
  }

  return symbolPlacement === 'before' ? "".concat(symbol).concat(extraSpace).concat(dollarAmount) : "".concat(dollarAmount).concat(extraSpace).concat(symbol);
};
/**
 * handleNegativeNumber - Helper function that places the minus sign in the correct position for a negative number
 *
 * @param formattedNumber - string (formatted dollar amount)

 * @returns string
 */


exports.addCurrencySymbol = addCurrencySymbol;

var handleNegativeNumber = function handleNegativeNumber() {
  var formattedNumber = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';

  if (formattedNumber.indexOf('-') !== -1) {
    return "-".concat(formattedNumber.replace('-', ''));
  }

  return formattedNumber;
};
/**
 * formatCurrency - Formats your currency based on the locale and currency code you provide
 *
 * NOTE: This is the function that you would actually use
 *
 * NOTE: When we open up a new market, this function should be updated to handle the new locales
 *
 * @param dollarAmount - integer - IMPORTANT: We assume an amount in PENNIES is passed as an argument. So for $3, you'd pass 300 as the dollarAmount value
 * @param locale - string
 * @param removeTrailingZeros - Boolean
 * @param showCurrencySymbol - Boolean - set to false if you just need the formatted number without a symbol
 * @param showCurrencySymbolAndCode - Boolean - set to true if you need a very explicit value, like $1,234.00 CAD. We won't add EUR to the end though because that's already very explicit
 *
 * @returns string
 */


exports.handleNegativeNumber = handleNegativeNumber;

var formatCurrency = function formatCurrency() {
  var dollarAmount = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  var locale = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'en_us';
  var removeTrailingZeros = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var showCurrencySymbol = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : true;
  var showCurrencySymbolAndCode = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;
  var correctedLocale = locale.replace('-', '_').toLowerCase();
  var formattedNumber;

  switch (correctedLocale) {
    case 'fr_ca':
      formattedNumber = formatNumberWithTwoDecimalPlaces(dollarAmount, ',');
      formattedNumber = addThousandsSeparator(formattedNumber, ',', '&nbsp;');
      formattedNumber = removeTrailingZeros ? removeZerosIfWholeNumber(formattedNumber, ',') : formattedNumber;
      formattedNumber = showCurrencySymbol ? addCurrencySymbol(formattedNumber, '$', 'after', true) : formattedNumber;
      formattedNumber = showCurrencySymbolAndCode ? addCurrencySymbol(formattedNumber, 'CAD', 'after', true) : formattedNumber;
      return handleNegativeNumber(formattedNumber);

    case 'en_uk':
      formattedNumber = formatNumberWithTwoDecimalPlaces(dollarAmount);
      formattedNumber = addThousandsSeparator(formattedNumber);
      formattedNumber = removeTrailingZeros ? removeZerosIfWholeNumber(formattedNumber) : formattedNumber;
      formattedNumber = showCurrencySymbol ? addCurrencySymbol(formattedNumber, '£') : formattedNumber;
      formattedNumber = showCurrencySymbolAndCode ? addCurrencySymbol(formattedNumber, 'GBP', 'after', true) : formattedNumber;
      return handleNegativeNumber(formattedNumber);

    case 'en_ie':
      formattedNumber = formatNumberWithTwoDecimalPlaces(dollarAmount);
      formattedNumber = addThousandsSeparator(formattedNumber);
      formattedNumber = removeTrailingZeros ? removeZerosIfWholeNumber(formattedNumber) : formattedNumber;
      formattedNumber = showCurrencySymbol ? addCurrencySymbol(formattedNumber, '€') : formattedNumber;
      return handleNegativeNumber(formattedNumber);

    case 'en_hk':
    case 'zh_hk':
      formattedNumber = formatNumberWithTwoDecimalPlaces(dollarAmount);
      formattedNumber = addThousandsSeparator(formattedNumber);
      formattedNumber = removeTrailingZeros ? removeZerosIfWholeNumber(formattedNumber) : formattedNumber;
      formattedNumber = showCurrencySymbol ? addCurrencySymbol(formattedNumber, 'HK$') : formattedNumber;
      return handleNegativeNumber(formattedNumber);

    case 'es_mx':
    case 'en_mx':
      formattedNumber = formatNumberWithTwoDecimalPlaces(dollarAmount);
      formattedNumber = addThousandsSeparator(formattedNumber);
      formattedNumber = removeTrailingZeros ? removeZerosIfWholeNumber(formattedNumber) : formattedNumber;
      formattedNumber = showCurrencySymbol ? addCurrencySymbol(formattedNumber) : formattedNumber;
      formattedNumber = showCurrencySymbolAndCode ? addCurrencySymbol(formattedNumber, 'MXN', 'after', true) : formattedNumber;
      return handleNegativeNumber(formattedNumber);

    case 'fr_fr':
    case 'en_fr':
      formattedNumber = formatNumberWithTwoDecimalPlaces(dollarAmount, ',');
      formattedNumber = addThousandsSeparator(formattedNumber, ',', '&nbsp;');
      formattedNumber = removeTrailingZeros ? removeZerosIfWholeNumber(formattedNumber, ',') : formattedNumber;
      formattedNumber = showCurrencySymbol ? addCurrencySymbol(formattedNumber, '€', 'after', true) : formattedNumber;
      return handleNegativeNumber(formattedNumber);

    case 'en_de':
    case 'de_de':
    case 'en_es':
    case 'es_es':
    case 'en_it':
    case 'it_it':
    case 'en_pt':
    case 'pt_pt':
      formattedNumber = formatNumberWithTwoDecimalPlaces(dollarAmount, ',');
      formattedNumber = addThousandsSeparator(formattedNumber, ',', '.');
      formattedNumber = removeTrailingZeros ? removeZerosIfWholeNumber(formattedNumber, ',') : formattedNumber;
      formattedNumber = showCurrencySymbol ? addCurrencySymbol(formattedNumber, '€', 'after', true) : formattedNumber;
      return handleNegativeNumber(formattedNumber);

    case 'en_ca':
      formattedNumber = formatNumberWithTwoDecimalPlaces(dollarAmount);
      formattedNumber = addThousandsSeparator(formattedNumber);
      formattedNumber = removeTrailingZeros ? removeZerosIfWholeNumber(formattedNumber) : formattedNumber;
      formattedNumber = showCurrencySymbol ? addCurrencySymbol(formattedNumber) : formattedNumber;
      formattedNumber = showCurrencySymbolAndCode ? addCurrencySymbol(formattedNumber, 'CAD', 'after', true) : formattedNumber;
      return handleNegativeNumber(formattedNumber);

    case 'en_au':
      formattedNumber = formatNumberWithTwoDecimalPlaces(dollarAmount);
      formattedNumber = addThousandsSeparator(formattedNumber);
      formattedNumber = removeTrailingZeros ? removeZerosIfWholeNumber(formattedNumber) : formattedNumber;
      formattedNumber = showCurrencySymbol ? addCurrencySymbol(formattedNumber) : formattedNumber;
      formattedNumber = showCurrencySymbolAndCode ? addCurrencySymbol(formattedNumber, 'AUD', 'after', true) : formattedNumber;
      return handleNegativeNumber(formattedNumber);

    case 'en_nz':
      formattedNumber = formatNumberWithTwoDecimalPlaces(dollarAmount);
      formattedNumber = addThousandsSeparator(formattedNumber);
      formattedNumber = removeTrailingZeros ? removeZerosIfWholeNumber(formattedNumber) : formattedNumber;
      formattedNumber = showCurrencySymbol ? addCurrencySymbol(formattedNumber) : formattedNumber;
      formattedNumber = showCurrencySymbolAndCode ? addCurrencySymbol(formattedNumber, 'NZD', 'after', true) : formattedNumber;
      return handleNegativeNumber(formattedNumber);

    case 'en_us':
    case 'es_us':
    default:
      formattedNumber = formatNumberWithTwoDecimalPlaces(dollarAmount);
      formattedNumber = addThousandsSeparator(formattedNumber);
      formattedNumber = removeTrailingZeros ? removeZerosIfWholeNumber(formattedNumber) : formattedNumber;
      formattedNumber = showCurrencySymbol ? addCurrencySymbol(formattedNumber) : formattedNumber;
      formattedNumber = showCurrencySymbolAndCode ? addCurrencySymbol(formattedNumber, 'USD', 'after', true) : formattedNumber;
      return handleNegativeNumber(formattedNumber);
  }
};

exports.formatCurrency = formatCurrency;
var _default = {
  formatNumberWithTwoDecimalPlaces: formatNumberWithTwoDecimalPlaces,
  addThousandsSeparator: addThousandsSeparator,
  removeZerosIfWholeNumber: removeZerosIfWholeNumber,
  addCurrencySymbol: addCurrencySymbol,
  handleNegativeNumber: handleNegativeNumber,
  formatCurrency: formatCurrency
};
exports.default = _default;